import { atom } from 'jotai'
import { isMatching, match, P } from 'ts-pattern'
import { openVerifyEmailModal } from 'src/components/Account/VerifyEmailModal'
import { openPaymentFailedDialog } from 'src/components/Membership/dialogs'
import { UserCancelled } from 'src/components/Modal/Confirm'
import Text from 'src/components/ui/Text'
import {
  translateErrorCode,
  translateOnerwayErrorCode,
} from 'src/utils/errorCodeTranslator'
import { getErrorCode } from 'src/utils/errorUtil'
import { track } from 'src/utils/tracker'
import { Currency, type Interval } from '../apollo/generated/graphql'
import { refetchGetMyMembership } from '../apollo/membership'
import type { MembershipPlan } from './membership'

export enum PaymentProvider {
  /** @deprecated unavailable */
  stripe = 'stripe',
  /** @deprecated unavailable */
  paddle = 'paddle',
  /** @deprecated unavailable */
  braintree = 'braintree',
  onerway = 'onerway',
  paypay = 'paypay',
  gmo = 'gmo',
  google = 'google3',
  apple = 'apple2',

  /*
   * not support in backend, just for frontend to
   * distinguish different onerway payment method
   */
  onerwayGooglePay = 'onerway_google_pay',
  onerwayJcbAeUnionPay = 'onerway_jcb_ae_union_pay',

  // Virtual provider — backend dispatches via paymentMethod under
  // provider=onerway. Desktop sends wechat_qr; mobile shows a desktop-only
  // notice and never calls execute().
  onerwayWechatPay = 'onerway_wechat_pay',

  internal = 'internal',
}

/**
 * Check if a payment provider is a mobile payment provider (Google Play or Apple App Store).
 */
export const isMobilePaymentProvider = isMatching(
  P.union(PaymentProvider.google, PaymentProvider.apple),
)

/**
 * Check if a payment provider is deprecated.
 */
export const isDeprecatedPaymentProvider = isMatching(
  P.union(
    PaymentProvider.stripe,
    PaymentProvider.paddle,
    PaymentProvider.braintree,
  ),
)

/**
 * Check if a payment provider only supports one-time purchases.
 *
 * @todo Make it not hardcoded
 */
export const isOneTimePurchaseOnlyProvider = isMatching(
  P.union(
    PaymentProvider.onerway,
    PaymentProvider.onerwayJcbAeUnionPay,
    PaymentProvider.paypay,
  ),
)

/**
 * `AllowedPaymentProviders` is a type that specifies a set of allowed payment providers to use. The elements provided will still be filtered against a list of actual available payment providers, and the filtered elements will decide the actual logic to take. The special value `any` will allow any available payment provider to be used.
 *
 * - 0 available providers: a "no available payment providers" error will be shown.
 *
 * - [not implemented] 1 available provider: the `children` of that provider will be automatically shown to the user.
 *
 * - 2 or more available providers: the user will be prompted to choose a provider, and the `children` of the chosen provider will then be shown.
 */
export type AllowedPaymentProviders = PaymentProvider[] | 'any'

export type PreferredPaymentProvider = {
  provider: PaymentProvider
}

export interface PaymentProviderOptions {
  allowed: AllowedPaymentProviders
  preferred?: PreferredPaymentProvider
}

export type TrackingOpts = {
  dialogTrackingOpts?: Record<string, any>
  trackingFnAfterFetch?: () => void
}

export interface PaymentOpts {
  itemId: string
  oneTimeMembership?: MembershipPlan
  interval?: Interval
  currency?: Currency
  /**
   * `prices` will only be displayed in the payment dialog and the actual amount to be paid will still be calculated by the server.
   * That being said, making sure `prices` are correct is still important, as discrepancies may lead to user confusions.
   */
  prices?: Record<Currency, number>
  /**
   * Plain url,
   */
  redirect?: string
  paymentProvider: PaymentProviderOptions

  shouldDiscount?: boolean
  afterCancelSurvey?: () => void
  /**
   * Boost option for coupon code to apply during payment
   */
  promotionOption?: { extraPackageBoostCode?: string }
  /**
   * The final credits amount the user will receive after purchase (including all boosts)
   * This is displayed in the payment dialog for user reference
   */
  credits?: number
  /**
   * Custom payment execution function. If not provided, the PaymentDialog
   * will default to IntentToPay GraphQL mutation.
   */
  executePayment?: PaymentExecuteFn
  /**
   * Initial provider selection for internally re-opened payment dialogs.
   */
  initialPaymentProvider?: PaymentProvider
}

export interface OpenPaymentDialogReturns {
  orderId: string
  finalizedPaymentProvider: PaymentProvider
}

export const handleChangePlanSuccess = async () => {
  // FIXME: success notice
  // toast.success('success')
  refetchGetMyMembership()
}

export type PaymentProviderError<P extends PaymentProvider = PaymentProvider> =
  Error & {
    paymentProvider: P
    paymentRawError?: unknown
    respCode?: string
    respMsg?: string
  }

const getPaymentErrorMessage = (errorMessage: string) => {
  return (
    <>
      <Text i18nKey="membership:payment.if-this-error-persists-pls-contact-us" />
      {' ('}
      <Text
        i18nKey="membership:payment.unexpected-error"
        values={{ error: errorMessage }}
      />
      {')'}
    </>
  )
}

const translateOnerwayError = (
  err: PaymentProviderError<PaymentProvider.onerway>,
  fallbackMessage?: string,
) => {
  const payload = err
  const respCode = payload.respCode ?? undefined
  const fallback =
    (typeof payload.respMsg === 'string' ? payload.respMsg : undefined) ??
    payload.message ??
    fallbackMessage
  return translateOnerwayErrorCode(respCode, {
    fallback,
  })
}

/**
 * Atom to track whether PaymentDialog is currently open.
 * Used by ForceRefreshChecker to skip refresh prompts during payment.
 */
export const isPaymentDialogOpenAtom = atom(false)

export function getCurrentPrice({
  selectedPaymentProvider,
  prices,
  currency,
}: {
  selectedPaymentProvider: PaymentProvider | null
  prices: PaymentOpts['prices']
  currency: PaymentOpts['currency']
}) {
  if (!selectedPaymentProvider) {
    return null
  }

  return match(selectedPaymentProvider)
    .with(PaymentProvider.paypay, () => ({
      currency: Currency.Jpy,
      amount: prices?.[Currency.Jpy] as number, // PayPay only supports JPY
    }))
    .with(PaymentProvider.gmo, () => ({
      currency: Currency.Jpy,
      amount: prices?.[Currency.Jpy] as number, // GMO CVS only supports JPY
    }))
    .otherwise(() => {
      const validCurrency = currency ?? Currency.Usd
      const amount = prices?.[validCurrency]
      if (amount === undefined) return null
      return { currency: validCurrency, amount }
    })
}

// ============= Payment Execute Abstraction =============

export interface PaymentExecuteResultOnerway {
  orderId: string
  transactionId: string
  subProductType?: string
  // WeChat Pay LPMS response, flattened by the executor:
  //   redirectUrl  → wechat_h5 redirect target (also card 3DS challenge)
  //   qrCode       → wechat_qr raw deeplink for qrcode.react
  //   qrCodeUrl    → wechat_qr hosted image URL (fallback)
  redirectUrl?: string
  qrCode?: string
  qrCodeUrl?: string
}

export interface PaymentExecuteResultPaypay {
  orderId: string
  url: string
  codeId?: string
  deepLink?: string
}

export interface PaymentExecuteResultGmo {
  orderId: string
  url?: string
  confNo: string
  receiptNo: string
  expiredAt: string
}

export type PaymentExecuteInput =
  | {
      provider: PaymentProvider.onerway
      providerOptions?: { paymentMethod?: string }
    }
  | {
      provider: PaymentProvider.onerwayJcbAeUnionPay
      providerOptions?: { paymentMethod?: string }
    }
  | {
      provider: PaymentProvider.onerwayWechatPay
      providerOptions: { paymentMethod: 'wechat_h5' | 'wechat_qr' }
    }
  | { provider: PaymentProvider.paypay }
  | {
      provider: PaymentProvider.gmo
      providerOptions: { paymentMethod: string; phoneNumber: string }
    }

export type PaymentExecuteFn = {
  (
    input: Extract<
      PaymentExecuteInput,
      {
        provider: PaymentProvider.onerway | PaymentProvider.onerwayJcbAeUnionPay
      }
    >,
  ): Promise<PaymentExecuteResultOnerway>
  (
    input: Extract<
      PaymentExecuteInput,
      { provider: PaymentProvider.onerwayWechatPay }
    >,
  ): Promise<PaymentExecuteResultOnerway>
  (
    input: Extract<PaymentExecuteInput, { provider: PaymentProvider.paypay }>,
  ): Promise<PaymentExecuteResultPaypay>
  (
    input: Extract<PaymentExecuteInput, { provider: PaymentProvider.gmo }>,
  ): Promise<PaymentExecuteResultGmo>
}

export const handlePaymentError = (err: any) => {
  // User Cancelled should not be a "Payment" error
  if (err === UserCancelled) return
  const errorCode = getErrorCode(err)
  const fallbackMessage =
    typeof err?.message === 'string' ? err.message : undefined
  const rawMessage =
    typeof err?.respMsg === 'string' ? err.respMsg : fallbackMessage
  const paymentProvider = (err as PaymentProviderError)?.paymentProvider
  const isOnerwayError = paymentProvider === PaymentProvider.onerway
  const vendorRespCode = isOnerwayError ? err.respCode : undefined
  const translatedErrorMessage = isOnerwayError
    ? translateOnerwayError(
        err as PaymentProviderError<PaymentProvider.onerway>,
        fallbackMessage,
      )
    : translateErrorCode(errorCode, {
        fallback: fallbackMessage,
      })
  match(errorCode)
    .with('EMAIL_IS_REQUIRED', () => {
      openVerifyEmailModal()
    })
    .otherwise(() => {
      const msg = getPaymentErrorMessage(translatedErrorMessage)
      track('payment_failed', {
        errorMessage: translatedErrorMessage,
        rawMessage,
        errorCode: vendorRespCode ?? errorCode,
        paymentProvider,
      })
      openPaymentFailedDialog(msg)
    })
}
